import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Presentation } from "lucide-react";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import api, { apiError } from "../lib/api";
import { slugify } from "../lib/deckModel";
import { DeckCard } from "../components/decks/DeckCard";
import { CreateDeckDialog } from "../components/decks/CreateDeckDialog";
import { AccessDialog } from "../components/decks/AccessDialog";

const STATUSES = ["Draft", "Internal Review", "Partner Review", "Investor Review", "City / Government Review", "Published", "Archived"];
const TYPES = ["Commercial", "Residential", "Mixed Use", "Public-Private"];

export default function Decks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [ptype, setPtype] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [accessDeck, setAccessDeck] = useState(null);
  const [del, setDel] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q); if (status) p.set("status", status); if (ptype) p.set("project_type", ptype);
    api.get(`/decks?${p}`).then((r) => setItems(r.data.items || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [q, status, ptype]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const onAction = async (kind, d) => {
    try {
      if (kind === "access") return setAccessDeck(d);
      if (kind === "delete") return setDel(d);
      if (kind === "duplicate") { await api.post(`/decks/${d.id}/duplicate`, {}); toast.success("Deck duplicated"); }
      if (kind === "publish") { const { data } = await api.post(`/decks/${d.id}/publish`, {}); toast.success(`Published — arqitek.co/${data.slug}`); }
      if (kind === "unpublish") { await api.post(`/decks/${d.id}/unpublish`); toast.success("Deck unpublished"); }
      if (kind === "archive") { await api.put(`/decks/${d.id}`, { status: d.status === "Archived" ? "Draft" : "Archived" }); toast.success(d.status === "Archived" ? "Deck restored" : "Deck archived"); }
      if (kind === "rename") {
        const name = window.prompt("Rename deck", d.name); if (!name) return;
        await api.put(`/decks/${d.id}`, { project: { ...d.project, name } }); toast.success("Renamed");
      }
      if (kind === "slug") {
        const s = window.prompt("New URL — arqitek.co/", d.slug); if (!s) return;
        await api.put(`/decks/${d.id}`, { slug: slugify(s) }); toast.success("URL updated");
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const chip = (active, label, onClick, testid) => (
    <button key={testid} onClick={onClick} data-testid={testid} className={`rounded-full border px-3 py-1 text-xs transition-colors ${active ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary/50"}`}>{label}</button>
  );

  return (
    <div className="px-4 sm:px-8 py-8 max-w-[1500px] mx-auto space-y-6" data-testid="decks-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary"><Presentation className="h-4 w-4" />Decks</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="decks-title">Project Decks</h1>
          <p className="mt-2 text-sm text-muted-foreground">Create, manage, publish, and share investor-ready project presentations.</p>
        </div>
        <Button size="lg" onClick={() => setCreateOpen(true)} className="rounded-full" data-testid="create-deck-btn"><Plus className="h-4 w-4 mr-1.5" />Create Deck</Button>
      </div>
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="relative lg:w-80">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project, city, state, county, APN, type" className="w-full rounded-full border border-border bg-secondary/30 pl-9 pr-4 py-2 text-sm outline-none focus:border-primary" data-testid="decks-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chip(!ptype, "All types", () => setPtype(""), "decks-type-all")}
          {TYPES.map((t) => chip(ptype === t, t, () => setPtype(ptype === t ? "" : t), `decks-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chip(!status, "All statuses", () => setStatus(""), "decks-status-all")}
          {STATUSES.map((s) => chip(status === s, s, () => setStatus(status === s ? "" : s), `decks-status-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`))}
        </div>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-16 text-center">Loading decks…</div>
        : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-20 text-center" data-testid="decks-empty">
            <Presentation className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">{q || status || ptype ? "No decks match these filters." : "No decks yet — create your first ARQITEK project deck."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5" data-testid="decks-grid">
            {items.map((d) => <DeckCard key={d.id} deck={d} onAction={onAction} />)}
          </div>
        )}
      <CreateDeckDialog open={createOpen} onOpenChange={setCreateOpen} decks={items} />
      {accessDeck && <AccessDialog key={accessDeck.id} deck={accessDeck} open onOpenChange={(o) => { if (!o) { setAccessDeck(null); load(); } }} onSaved={setAccessDeck} />}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="deck-delete-confirm">
          <AlertDialogHeader><AlertDialogTitle>Delete “{del?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This permanently deletes the deck and frees arqitek.co/{del?.slug}. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deck-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { try { await api.delete(`/decks/${del.id}`); toast.success("Deck deleted"); setDel(null); load(); } catch (e) { toast.error(apiError(e)); } }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="deck-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
